import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { Plus } from 'src/features/business/shared/assets/plus'
import { default as _ChatButton } from 'src/features/chats/components/buttons/global-chat-button'
import { Invite as _Invite } from 'src/shared/components/icons/invite'
import { SubscriptionLogo as _SubscriptionLogo } from 'src/shared/components/icons/logos/subscription'
import { MatchPeople as _MatchPeople } from 'src/shared/components/icons/match-people'
import { BellFilled } from 'src/shared/components/icons/notifications/bell-filled'
import { BellOutline } from 'src/shared/components/icons/notifications/bell-outline'
import { BusinessFilledNew } from 'src/shared/components/icons/sidebar/business-filled-new'
import { BusinessOutlineNew } from 'src/shared/components/icons/sidebar/business-outline-new'
//import { House } from 'src/shared/components/icons/sidebar/house'
import { HouseFilled } from 'src/shared/components/icons/sidebar/house-filled'
import { HouseOutlinedNew } from 'src/shared/components/icons/sidebar/house-outlined-new'
import { InviteNew } from 'src/shared/components/icons/sidebar/invite-new'
import { SettingsNew } from 'src/shared/components/icons/sidebar/settings-new'
import { Button } from 'src/shared/components/ui/buttons/button'
import { SearchInput } from 'src/shared/components/ui/inputs/search-input'
import { Badge } from 'src/shared/components/ui/miscellaneous/badge'
import { ScrollArea as _ScrollArea } from 'src/shared/components/ui/miscellaneous/scroll-area'
import {
  Sheet as _Sheet,
  SheetContent as _SheetContent,
  SheetHeader as _SheetHeader,
  SheetFooter as _SheetFooter,
  SheetTrigger as _SheetTrigger,
} from 'src/shared/components/ui/popups/sheet'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { UserCard as _UserCard } from 'src/shared/features/users/components/cards/user-card'
import { ProfileAvatar } from 'src/shared/features/users/components/profile-photos/profile-avatar'
import { PercentSection as _PercentSection } from 'src/shared/features/users/components/view-profile/percent-section'
import { VisibilityCard as _VisibilityCard } from 'src/shared/features/visibility/components/card/scorebar'
import { cn } from 'src/shared/utils/cn'

import type { NavItem, mobileMenuItemsTop } from './nav-config'
import { mobileMenuItemsBottom as _mobileMenuItemsBottom } from './nav-config'

const NavMobile = ({
  hideHeader = false,
  hideFooter = false,
  onSearch,
}: {
  hideHeader?: boolean
  hideFooter?: boolean
  onSearch?: (query: string) => void
}) => {
  const [_isSheetOpen, _setIsSheetOpen] = useState(false)
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const { data: currentUser } = useMyUserData()
  const location = useLocation()
  const navigate = useNavigate()

  const handleSearchChange = (value: string) => {
    onSearch?.(value)
    setIsSearchFocused(!!value)
  }

  const shouldShowItem = (item: NavItem) => {
    if (item.requiredRole) {
      return currentUser?.role === item.requiredRole
    }
    return true
  }

  const _renderNavItem = (item: (typeof mobileMenuItemsTop)[0]) => {
    if (!shouldShowItem(item)) return null

    const isActive = location.pathname.includes(item.url.replace('/', ''))
    const Icon = isActive && item.activeIcon ? item.activeIcon : item.defaultIcon

    const content = (
      <div key={item.url} className={'flex flex-row items-center gap-3 text-lg font-medium'}>
        <Icon
          className={cn(
            item.title === 'Business' && 'h-5 w-[21.433px]',
            item.title === 'Feed' && 'h-5 w-[20.638px]',
            item.title === 'Settings' && 'size-10',
            !['Business', 'Feed', 'Settings'].includes(item.title) && 'size-6',
          )}
        />
        {item.title}
        {item.comingSoon && <Badge text="SOON" variant="outline" className="ml-auto" />}
      </div>
    )

    return item.comingSoon ? (
      content
    ) : (
      <Link key={item.url} to={item.url} onClick={() => _setIsSheetOpen(false)}>
        {content}
      </Link>
    )
  }

  const _handleSendReferralClick = () => {
    _setIsSheetOpen(false)
    navigate(paths.home.getHref())
  }

  const handleInviteClick = () => {
    _setIsSheetOpen(false)
    navigate(paths.invite.getHref())
  }

  if (!currentUser) {
    return null
  }

  return (
    <>
      {!hideHeader && (
        <nav
          className="fixed left-0 top-0 z-50 h-mobile-header w-full bg-background"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mx-auto flex h-full items-center gap-2 px-4">
            {/* Search Bar - First */}
            <div
              className={cn(
                'relative flex-1 transition-all duration-200',
                isSearchFocused ? 'flex-grow' : 'w-[256px]',
                'ml-0 md:ml-6',
              )}
            >
              <SearchInput
                onChange={handleSearchChange}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                placeholder="Search or ask anything"
                className="h-10 text-left text-sm"
                searchTerm=""
              />
            </div>

            {/* Invite Icon - Second */}
            {/* Previous versions:
            size-6: <InviteNew className="size-6" />
            size-8: <InviteNew className="size-8" />
            */}
            {!isSearchFocused && (
              <Button variant="ghost" size="icon" onClick={handleInviteClick}>
                <InviteNew className="size-10" />
              </Button>
            )}

            {/*  - Third */}
            {/* {!isSearchFocused && (
              <Sheet open={_isSheetOpen} onOpenChange={_setIsSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <ProfileAvatar userId={currentUser.id} size={40} />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="flex w-[300px] flex-col justify-between px-8 py-4 sm:w-[400px]">
                  <SheetHeader className="flex flex-col gap-3">
                    <SubscriptionLogo />

                    <div className="flex flex-col gap-5">{mobileMenuItemsTop.map((item) => _renderNavItem(item))}</div>

                    <div className="flex flex-col gap-3">
                      <Button variant="default" className="rounded-3xl" onClick={_handleSendReferralClick}>
                        Match / Refer
                      </Button>
                      <Button
                        variant="secondaryInverse"
                        className="flex w-full items-center justify-center gap-2 rounded-full"
                        onClick={handleInviteClick}
                      >
                        <Invite className="size-6" />
                        Invite
                      </Button>
                      <Button
                        variant="outline"
                        leftIcon={<AiLightning className="size-4" />}
                        className="flex w-full items-center justify-center rounded-full border-secondary"
                        onClick={() => navigate(paths.assistant.path)}
                      >
                        Chat with Lynk
                      </Button>
                    </div>
                  </SheetHeader>
                  <ScrollArea startAtBottom>
                    <SheetFooter className="flex w-full flex-col gap-4">
                      <div className="flex w-full flex-col gap-4">
                        {mobileMenuItemsBottom.filter(shouldShowItem).map((item) => renderNavItem(item))}
                        {currentUser && <PercentSection user={currentUser} variant="sidebar" />}
                        {currentUser && <VisibilityCard />}
                        {currentUser && (
                          <button
                            onClick={() => navigate(paths.users.profile.getHref(currentUser.username))}
                            className="w-full py-2 transition-opacity hover:opacity-80"
                          >
                            <UserCard
                              userId={currentUser.id}
                              buttonType="none"
                              className="w-full"
                              isMe={true}
                              showUsername={false}
                              showAddress={false}
                            />
                          </button>
                        )}
                      </div>
                    </SheetFooter>
                  </ScrollArea>
                </SheetContent>
              </Sheet>
            )} */}

            {/* Settings Button - Fourth */}
            {!isSearchFocused && (
              <Button variant="ghost" size="icon" onClick={() => navigate('/settings')}>
                <SettingsNew className="size-10" />
              </Button>
            )}
          </div>
        </nav>
      )}
      {!hideFooter && (
        <nav className="fixed bottom-0 left-0 z-50 w-full bg-background pb-3">
          <div className="relative mx-auto flex h-full items-center justify-between px-4">
            <div className="flex w-2/5 justify-around">
              <Link
                to={paths.business.path}
                className="flex flex-col items-center justify-center gap-1 text-foreground"
              >
                {location.pathname.includes(paths.business.path) ? (
                  <BusinessFilledNew className="h-5 w-[21.433px]" />
                ) : (
                  <BusinessOutlineNew className="h-5 w-[21.433px]" />
                )}
                <span
                  className={`text-center text-[10px] font-semibold leading-[10px] text-[#1B3029] ${
                    location.pathname.includes(paths.business.path) ? 'font-bold' : ''
                  }`}
                >
                  Business
                </span>
              </Link>
              <Link to={paths.feed.path} className="flex flex-col items-center justify-center gap-1 text-foreground">
                {location.pathname.includes(paths.feed.path) ? (
                  <HouseFilled className="h-5 w-[20.638px]" />
                ) : (
                  <HouseOutlinedNew className="h-5 w-[20.638px]" />
                )}
                <span
                  className={`text-center text-[10px] font-semibold leading-[10px] text-[#1B3029] ${
                    location.pathname.includes(paths.feed.path) ? 'font-bold' : ''
                  }`}
                >
                  Feed
                </span>
              </Link>
            </div>
            <div className="absolute bottom-2 left-1/2 flex shrink-0 -translate-x-1/2 flex-col items-center gap-2">
              <div className="flex size-10 items-center justify-center rounded-full bg-primary text-white shadow-[0_0_7px_1px_#45D88E] transition-all duration-200 hover:scale-110 hover:cursor-pointer">
                <Plus className="size-5" />
              </div>
            </div>
            <div className="flex w-2/5 justify-around">
              <Link
                to={paths.notifications.path}
                className="flex flex-col items-center justify-center gap-1 text-foreground"
              >
                {location.pathname.includes(paths.notifications.path) ? (
                  <BellFilled className="h-[20.118px] w-[18px]" />
                ) : (
                  <BellOutline className="h-[20.118px] w-[18px]" />
                )}
                <span
                  className={`text-center text-[10px] font-semibold leading-[10px] text-[#1B3029] ${
                    location.pathname.includes(paths.notifications.path) ? 'font-bold' : ''
                  }`}
                >
                  Activity
                </span>
              </Link>
              <Link
                to={paths.assistant.path}
                className="flex flex-col items-center justify-center gap-1 rounded-lg p-2"
              >
                <ProfileAvatar userId={currentUser.id} size={24} className="size-6" />
                <span
                  className={`text-center text-[10px] font-semibold leading-[10px] text-[#1B3029] ${
                    location.pathname.includes(paths.assistant.path) ? 'font-bold' : ''
                  }`}
                >
                  Menu
                </span>
              </Link>
            </div>
          </div>
        </nav>
      )}
    </>
  )
}

export default NavMobile
